import {memo, useCallback, useEffect, useRef, useState} from 'react';

import {isEmpty} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';

import {CentralLoading} from '~/shared/components/Loaders';
import AddCreditCard from '~/shared/components/AddCreditCard';
import {trackEvent} from '~/shared/services/analytics';
import {handleRefreshToken} from '~/shared/services/auth';
import {MoneycardOtlRuleManagement} from '~/shared/store/models';
import {AddCreditCardModes} from '~/shared/consts/checkoutConsts';
import {getAvailablePayments} from '~/shared/store/storeModules/payments/paymentsActions';
import {navigateToDefaultStartPage} from '~/shared/services/navigation';
import {getMoneyCardAndCreditCardFromPayments, isAvailablePayments} from '~/shared/utils/payments/payments';
import {MoneycardAndCreditcard, PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import {createLogger} from '~/shared/logging';
import {is401Error} from '~/shared/services/apiErrorService';
import {selectUserData} from '~/shared/store/storeModules/user';
import {selectA11yContrast} from '~/shared/store/storeModules/a11y/a11ySelectors';
import {OtlMode} from '~/shared/components/Otl/types';
import {getOtlRuleManagement} from '~/shared/services/bffService/api';

import OtlRuleSettings from './OtlRuleSettings';
import {getMoneycardOtlRuleManagementData, getOtlRuleCardId} from './utils';

const logger = createLogger('OtlSectionsContainer');

const OTL_STEP_MODES = {
  [OtlMode.RULES_SETTINGS]: OtlRuleSettings,
  [OtlMode.ADD_CREDIT_CARD]: AddCreditCard,
};

const BACK_LINK_CLICK_HANDLER: Record<OtlMode, (options: {
  hasVisitedOtlRules: boolean;
  setNewOtlMode: (mode: OtlMode) => void;
  onClose?: () => void;
}) => void> = {
  [OtlMode.ADD_CREDIT_CARD]: ({hasVisitedOtlRules, setNewOtlMode, onClose}) => {
    if (hasVisitedOtlRules) {
      setNewOtlMode(OtlMode.RULES_SETTINGS);
    } else {
      if (onClose) {
        onClose();
      }
    }
  },
  [OtlMode.RULES_SETTINGS]: ({onClose}) => {
    if (onClose) {
      onClose();
    }
  },
};

type OtlSectionsContainerProps = {
  onClose?: () => void;
  onOtlSuccess?: () => void;
  onModeChange?: (nextMode: OtlMode) => void;
  orderCardId?: string;
  withoutHeader?: boolean;
  withoutCreditCardsList?: boolean;
  hideLimitations?: boolean;
  showBackArrow?: boolean;
  showUnlinkButton?: boolean;
  onOtlFailure?: () => void;
  onUnlinkSuccess?: (() => void) | (() => Promise<void>);
};

const OtlSectionsContainer = ({
  onClose,
  onOtlSuccess,
  onModeChange,
  orderCardId,
  withoutHeader,
  withoutCreditCardsList,
  hideLimitations,
  showBackArrow = true,
  showUnlinkButton,
  onOtlFailure,
  onUnlinkSuccess,
}: OtlSectionsContainerProps) => {
  const dispatch = useDispatch();
  const [otlMode, setOtlMode] = useState(OtlMode.RULES_SETTINGS);
  const [hasVisitedOtlRules, setHasVisitedOtlRules] = useState(false);
  const [otlData, setOtlData] = useState<MoneycardOtlRuleManagement | null>(null);
  const [selectedCreditCard, setSelectedCreditCard] = useState<number | null>(null);
  const userData = useSelector(selectUserData);
  const withContrastFilter = useSelector(selectA11yContrast);

  const [cardId, setCardId] = useState<string | undefined>(orderCardId);

  const paymentsRequestControlRef = useRef<{hasSentRequest: boolean}>({hasSentRequest: false});

  const setNewOtlMode = useCallback((nextMode: OtlMode) => {
    setOtlMode(nextMode);
    onModeChange?.(nextMode);
  }, [onModeChange]);

  const getPayments = useCallback(async () => {
    if (!userData?.userId) {
      return;
    }

    const payments = await dispatch(getAvailablePayments());
    if (!isAvailablePayments(payments)) {
      logger.error('Unexpected error');
      navigateToDefaultStartPage();
      return;
    }

    const {Moneycard: moneycard, Creditcard: creditcard} = payments?.availablePayments.reduce<MoneycardAndCreditcard>(
      (paymentsProcessedData, payment) => getMoneyCardAndCreditCardFromPayments(paymentsProcessedData, payment, userData.userId),
      {[PaymentMethodTypes.MONEY_CARD]: null, [PaymentMethodTypes.CREDIT_CARD]: null},
    );

    if (!moneycard || !creditcard) {
      navigateToDefaultStartPage();
      return;
    }

    setCardId(moneycard.encryptedMoneycardId);
  }, [dispatch, userData]);

  const onAddCreditCardSuccess = async () => {
    try {
      if (!cardId) {
        logger.warn('Moneycard wasnt provided to addCreditCard');
        return;
      }

      const data = await getOtlRuleManagement(`${cardId}`);

      const sortedCards = data?.userCreditcards?.sort((a, b) => b.creditcardId - a.creditcardId);
      if (sortedCards) {
        setSelectedCreditCard(sortedCards[0].creditcardId);
      }
      setHasVisitedOtlRules(true);
      setNewOtlMode(OtlMode.RULES_SETTINGS);
      setOtlData(data);
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, onAddCreditCardSuccess);
      }
    }
  };

  const addCreditCard = useCallback(() => {
    setNewOtlMode(OtlMode.ADD_CREDIT_CARD);
    trackEvent('hasClickedAddCreditCard', {linkType: 'Payments Modal'});
  }, [setNewOtlMode]);

  const onBackLinkClick = useCallback(() => {
    BACK_LINK_CLICK_HANDLER[otlMode]({hasVisitedOtlRules, onClose, setNewOtlMode});
  }, [otlMode, hasVisitedOtlRules, onClose, setNewOtlMode]);

  const ModeComponent = OTL_STEP_MODES[otlMode];
  const onSuccess = otlMode === OtlMode.RULES_SETTINGS ? onOtlSuccess : onAddCreditCardSuccess;

  useEffect(() => {
    if (!cardId) {
      if (!paymentsRequestControlRef.current.hasSentRequest) {
        paymentsRequestControlRef.current.hasSentRequest = true;
        getPayments();
      }
      return;
    }

    (async () => {
      const moneycardOtlRuleManagementData = await getMoneycardOtlRuleManagementData(cardId);

      if (moneycardOtlRuleManagementData) {
        let mode = OtlMode.ADD_CREDIT_CARD;
        if (!isEmpty(moneycardOtlRuleManagementData.userCreditcards)) {
          mode = OtlMode.RULES_SETTINGS;
          setHasVisitedOtlRules(true);
        }
        setNewOtlMode(mode);
        setOtlData(moneycardOtlRuleManagementData);

        setSelectedCreditCard(getOtlRuleCardId(moneycardOtlRuleManagementData));
      }
    })();
  }, [cardId, getPayments, setNewOtlMode]);

  if (isEmpty(otlData)) {
    return <CentralLoading />;
  }
  return (
    <ModeComponent
      otlData={otlData}
      moneycardId={cardId || ''}
      addCreditCard={addCreditCard}
      selectedCreditCard={selectedCreditCard}
      setSelectedCreditCard={setSelectedCreditCard}
      onClose={onClose}
      onSuccess={onSuccess}
      onBackLinkClick={onBackLinkClick}
      withoutHeader={withoutHeader}
      mode={AddCreditCardModes.MINIMAL}
      withoutCreditCardsList={withoutCreditCardsList}
      hideLimitations={hideLimitations}
      withContrastFilter={withContrastFilter}
      showBackArrow={showBackArrow}
      showUnlinkButton={showUnlinkButton}
      onOtlFailure={onOtlFailure}
      onUnlinkSuccess={onUnlinkSuccess}
    />
  );
};
export default memo(OtlSectionsContainer);
